import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import {
  MESSAGE_PROTOCOL_VERSION,
  MIN_SUPPORTED_VERSION,
  ReceiveMessageType,
  SendMessageType,
} from 'src/@types/message';
import { isVersionSupported, parseReceiveMessage } from 'src/@types/message.schema';
import { isNativeApp } from 'src/utils/environment';

declare global {
  interface DocumentEventMap {
    message: MessageEvent;
  }
}

type MessageHandler = (message: ReceiveMessageType) => void;

// SSR 안전한 useLayoutEffect (CRA에서는 항상 useLayoutEffect 사용)
const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

// @MX:ANCHOR: [AUTO] sendMessage는 여러 컴포넌트에서 사용되는 공통 메시지 전송 함수
// @MX:REASON: fan_in >= 3 (AppMessageListener, Craving, DrJin 등)
// @MX:SPEC: SPEC-WEBVIEW-001
export const sendMessage = (message: SendMessageType): void => {
  // 버전 정보를 메시지에 자동 추가
  const messageWithVersion = {
    ...message,
    version: MESSAGE_PROTOCOL_VERSION,
  };
  window.ReactNativeWebView?.postMessage(JSON.stringify(messageWithVersion));
};

/**
 * React Native WebView 통신을 위한 커스텀 훅
 * - 5단계 메시지 검증 파이프라인 적용
 * - React Native WebView에서 postMessage로 보낸 메시지를 받아서 처리
 * - sendMessage 함수를 통해 React Native WebView로 메시지 전송
 * @param handler 메시지를 받아서 처리할 함수
 */
const useWebViewCommunication = (handler?: MessageHandler) => {
  const nativeApp = isNativeApp();

  // ref 패턴으로 stale closure 방지
  const handlerRef = useRef<MessageHandler | undefined>(handler);
  useIsomorphicLayoutEffect(() => {
    handlerRef.current = handler;
  });

  const handleMessage = useCallback((event: MessageEvent) => {
    try {
      // Step 1: 원시 데이터 검증
      if (!event.data || typeof event.data !== 'string') return;

      // Step 2: JSON 파싱
      let parsed: unknown;
      try {
        parsed = JSON.parse(event.data);
      } catch {
        return;
      }

      // Step 3: Zod 스키마 검증
      const validated = parseReceiveMessage(parsed);
      if (!validated) return;

      // Step 4: 프로토콜 버전 호환성 확인
      if ('version' in validated && validated.version) {
        if (!isVersionSupported(validated.version, MIN_SUPPORTED_VERSION)) {
          console.warn(
            `[WebView] 지원하지 않는 메시지 버전: ${validated.version} (최소: ${MIN_SUPPORTED_VERSION})`,
          );
          return;
        }
      }

      // Step 5: 핸들러에 검증된 메시지 전달
      handlerRef.current?.(validated as ReceiveMessageType);
    } catch (error) {
      // 예상치 못한 오류 무시
    }
  }, []);

  useEffect(() => {
    document.addEventListener('message', handleMessage); // Android용
    window.addEventListener('message', handleMessage); // iOS용

    return () => {
      document.removeEventListener('message', handleMessage);
      window.removeEventListener('message', handleMessage);
    };
  }, [handleMessage]);

  return { sendMessage, isNativeApp: nativeApp };
};

export default useWebViewCommunication;
